/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { initializeFaro, FetchTransport, getWebInstrumentations } from '@grafana/faro-web-sdk';
import { TracingInstrumentation } from '@grafana/faro-web-tracing';
import { ENVIRONMENT } from 'config';

/**
 * Initialized Faro client.
 * This module is the only place that should directly interact with the Faro SDK.
 * Logging can be disabled via `ENVIRONMENT.ENABLE_FARO`.
 */
export const faro = initializeFaro({
  transports: [
    new FetchTransport({
      url: ENVIRONMENT.FARO_URL,
      bufferSize: 1000,
      concurrency: 5,
      defaultRateLimitBackoffMs: 1000,
    }),
  ],
  paused: ENVIRONMENT.ENABLE_FARO.toLowerCase() !== 'true',
  app: {
    name: ENVIRONMENT.FARO_APP_NAME,
    version: ENVIRONMENT.FARO_APP_VERSION,
    environment: ENVIRONMENT.FARO_ENV,
  },
  instrumentations: [
    ...getWebInstrumentations(),
    ...(ENVIRONMENT.ENABLE_FARO_TRACING?.toLowerCase() === 'true'
      ? [
          new TracingInstrumentation({
            resourceAttributes: {
              // OpenTelemetry resource attributes identifying this frontend service.
              // These are attached to every trace/spans and shown in Grafana.
              'service.name': ENVIRONMENT.FARO_APP_NAME,
              'service.version': ENVIRONMENT.FARO_APP_VERSION,
              'deployment.environment': ENVIRONMENT.FARO_ENV,
              'service.namespace': ENVIRONMENT.FARO_NAMESPACE,
            },
            instrumentationOptions: {
              // Requests to these URLs get the `traceparent` header attached automatically,
              // so the backend can join the same distributed trace.
              propagateTraceHeaderCorsUrls: ENVIRONMENT.BACKEND_API
                ? [new RegExp(ENVIRONMENT.BACKEND_API)]
                : [],
            },
          }),
        ]
      : []),
  ],
});

/**
 * Returns the OpenTelemetry tracer registered by Faro's TracingInstrumentation.
 * Use this to create custom spans around business logic that isn't
 * automatically captured by the default fetch/xhr/document-load instrumentations.
 *
 * @param {string} [name='frontend'] - Tracer name, typically the app or module name.
 * @returns {import('@opentelemetry/api').Tracer|undefined}
 */
export function getFaroTracer(name = 'frontend') {
  return faro?.api?.getOTEL()?.trace.getTracer(name);
}

/**
 * Builds a W3C `traceparent` header value from the currently active OpenTelemetry span.
 * Use this to manually attach trace context to requests that aren't covered by the
 * automatic fetch/XHR instrumentation (e.g. axios calls to hosts outside
 * `propagateTraceHeaderCorsUrls`, WebSocket messages, or custom transports).
 *
 * Format: `00-<trace-id>-<span-id>-<trace-flags>` per the W3C Trace Context spec.
 *
 * @returns {string|undefined} The `traceparent` header value, or undefined if no active span.
 */
export function getTraceParent() {
  const otel = faro?.api?.getOTEL();
  const span = otel?.trace.getActiveSpan();
  if (!span) {
    return undefined;
  }
  const { traceId, spanId, traceFlags } = span.spanContext();
  const flags = traceFlags.toString(16).padStart(2, '0');
  return `00-${traceId}-${spanId}-${flags}`;
}

/**
 * Sets the current Faro user context.
 * This is gated by `ENVIRONMENT.ENABLE_FARO` and is intentionally kept here
 * so other modules don't need to interact with the Faro SDK object directly.
 *
 * @param {{ username?: string }} user - User identity used for Faro correlation.
 */
export function faroSetUser(user) {
  if (ENVIRONMENT.ENABLE_FARO.toLowerCase() === 'true' && user?.username) {
    faro?.api?.setUser({ username: user.username });
  }
}

/**
 * creates a new log
 * @param {string} msg - log message.
 * @param {LogLevel} level - log level
 * @param {Object.<string, number>} metaObj - additional meta data for the log
 */
export default function faroLog(msg, level, metaObj) {
  if (ENVIRONMENT.ENABLE_FARO.toLowerCase() === 'true') {
    faro?.api.pushLog([msg], { level, context: metaObj });
  }
}

/**
 * send a custom event to grafana alloy for user activeness visualization
 * @param {number} activeness - user activeness
 * @param {number} activeTime - user active time
 * @param {number} loggedInFor - total logged in time
 * @param {number} loggedInTime - time the user logged in
 */
// eslint-disable-next-line no-unused-vars
export function faroSendActiveUserSession(activeness, activeTime, loggedInFor, loggedInTime) {
  if (ENVIRONMENT.ENABLE_FARO.toLowerCase() === 'true') {
    faro?.api.pushEvent(
      'user_active_time_calculation',
      {
        activeness: activeness.toString(),
        activeTime: activeTime.toString(),
        loggedInFor: loggedInFor.toString(),
        loggedInTime,
        loggedOutTime: new Date().toJSON(),
      },
      'active-user'
    );
  }
}

/**
 * send a custom event to visualize when users are active
 * @param {number} time - current time
 * @param {number} chunkDuration - chunk duration
 */
export function faroSendActiveChunks(time, chunkDuration) {
  if (ENVIRONMENT.ENABLE_FARO.toLowerCase() === 'true') {
    // this is to set the spike
    faro?.api.pushMeasurement({
      type: 'user_active_windows',
      values: {
        time,
        value: 1,
      },
    });

    // this is to set the line to false after the chunk window
    faro?.api.pushMeasurement({
      type: 'user_active_windows',
      values: {
        time: time + chunkDuration,
        value: 0,
      },
    });
  }
}

/**
 * send a custom event to visualize when users log in time and logout time
 * @param {number|string} loggedInTime - logged in time
 * @param {number|string} loggedOutTime - log out time
 */
export function faroSendLoginSignal(loggedInTime, loggedOutTime) {
  if (ENVIRONMENT.ENABLE_FARO.toLowerCase() === 'true') {
    const isSessionTerminated = loggedOutTime === '' ? 'false' : 'true';
    faro?.api.pushEvent(
      'login_signal',
      {
        loggedInTime: loggedInTime.toString(),
        loggedOutTime: loggedOutTime.toString(),
        isSessionTerminated,
      },
      'logging-time'
    );
  }
}
/**
 * records the hotel selection session id
 * @param {*} hotelLabel
 * @returns
 */
export function recordHotelSelection(hotelLabel) {
  const hotelSelectionSessionId = Math.random().toString(36).substring(2, 15);
  if (hotelLabel) {
    localStorage.setItem('hotelActivitySession', `${hotelSelectionSessionId}:${hotelLabel}`);
  }
  return hotelSelectionSessionId;
}
/**
 * sends the hotel selection event to faro
 * @param {*} hotel
 * @param {*} terminateSession
 */
export function faroSendHotelBasedUserActivity(hotel, terminateSession = false) {
  if (ENVIRONMENT.ENABLE_FARO.toLowerCase() === 'true') {
    const hotelActivitySession = localStorage.getItem('hotelActivitySession');
    if (hotelActivitySession) {
      // send the end signal
      const [session, hotelName] = hotelActivitySession.split(':');
      faro?.api.pushEvent(
        'user_select_hotel',
        { hotelName, selection_session: session },
        'user-activity',
        { skipDedupe: true }
      );
    }

    if (!terminateSession && hotel !== null) {
      // generate new session
      const hotelSelectionSessionId = recordHotelSelection(hotel?.label);

      faro?.api.pushEvent(
        'user_select_hotel',
        { hotelName: hotel.label, selection_session: hotelSelectionSessionId },
        'user-activity',
        { skipDedupe: true }
      );
    }

    if (terminateSession) {
      localStorage.removeItem('hotelActivitySession');
    }
  }
}
